import React from "react";
import { Group, PinInput as ChakraPinInput } from "@chakra-ui/react";
import type { ComponentProps } from "react";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import type { PinInputSharedProps } from "./PinInput.props";

type ChakraRootProps = ComponentProps<typeof ChakraPinInput.Root>;

export type PinInputProps = PinInputSharedProps &
  Omit<
    ChakraRootProps,
    | "value"
    | "defaultValue"
    | "onValueChange"
    | "onValueComplete"
    | "invalid"
    | "disabled"
    | "placeholder"
    | "size"
    | "variant"
  >;

export default function PinInput({
  children,
  autoFocus = true,
  fullWidth = false,
  isInvalid,
  invalid,
  isDisabled,
  disabled,
  onComplete,
  onChange,
  placeholder = "",
  variant = "outline",
  dataTestId = "pin-input",
  value,
  defaultValue,
  size,
  isRequired: _isRequired,
  mask,
  otp,
  ...props
}: PinInputProps): JSX.Element {
  // Map v1/v2 props to v3 props for backward compatibility
  const invalidValue = invalid !== undefined ? invalid : isInvalid;
  const disabledValue = disabled !== undefined ? disabled : isDisabled;
  const firstField = React.useRef<HTMLInputElement>(null);

  const childCount = React.Children.count(children);
  const count = props.count ?? (childCount > 0 ? childCount : 4);

  // Pad the value array to the full count length
  const valueArray =
    value !== undefined
      ? Array.from({ length: count }, (_, i) => value[i] ?? "")
      : undefined;
  const defaultValueArray =
    defaultValue !== undefined
      ? Array.from({ length: count }, (_, i) => defaultValue[i] ?? "")
      : undefined;

  const handleComplete = (value: string) => {
    if (onComplete) {
      onComplete(value);
      // reset focus to first input field
      firstField.current?.focus();
    }
  };

  // Map variant for Chakra v3 compatibility
  // Chakra v3 PinInput only supports: "outline" | "flushed" | "subtle"
  let variantValue: "outline" | "flushed" | "subtle" | undefined;
  if (variant === "filled") {
    variantValue = "subtle";
  } else if (variant === "unstyled") {
    variantValue = "outline";
  } else {
    variantValue = variant;
  }

  return (
    <Flex flexDirection="row" justifyContent="space-evenly" width="100%">
      <ChakraPinInput.Root
        autoFocus={autoFocus}
        invalid={invalidValue}
        disabled={disabledValue}
        placeholder={placeholder}
        unstyled={variant === "unstyled" ? true : undefined}
        variant={variantValue}
        data-testid={dataTestId}
        count={count}
        value={valueArray}
        defaultValue={defaultValueArray}
        size={size}
        mask={mask}
        otp={otp}
        onValueChange={(details) => {
          onChange?.(details.value.join(""));
        }}
        onValueComplete={(details) => {
          handleComplete(details.value.join(""));
        }}
        {...props}
      >
        <ChakraPinInput.HiddenInput />
        <ChakraPinInput.Control asChild>
          {/* eslint-disable-next-line showpass-custom/no-hardcoded-style-values -- needed for xs breapoint without ts error */}
          <Group gap={["xs", "md"]} width="100%" minW="none">
            {React.Children.count(children) > 0
              ? React.Children.map(children, (child, index) => {
                  if (!React.isValidElement(child)) return child;

                  const injectedProps: Record<string, unknown> = {
                    index,
                    ...(fullWidth
                      ? { flexGrow: 1, flexBasis: "100%", maxWidth: "100%" }
                      : {}),
                  };

                  if (index === 0) {
                    injectedProps.ref = firstField;
                  }

                  return React.cloneElement(
                    child as React.ReactElement,
                    injectedProps
                  );
                })
              : Array.from({ length: count }).map((_, index) => (
                  <ChakraPinInput.Input
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    index={index}
                    ref={index === 0 ? firstField : undefined}
                    flexGrow={fullWidth ? 1 : undefined}
                    flexBasis={fullWidth ? "100%" : undefined}
                    maxWidth={fullWidth ? "100%" : undefined}
                  />
                ))}
          </Group>
        </ChakraPinInput.Control>
      </ChakraPinInput.Root>
    </Flex>
  );
}
